// src/pages/NoticeManagePage.tsx
import React, { useMemo, useState, useEffect } from "react";
import useSWR from "swr";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";

import NoticeFilterBar from "src/components/notice/NoticeFilterBar";
import NoticeListTable from "src/components/notice/NoticeListTable";
import NoticeUpsertModal from "src/components/notice/NoticeUpsertModal";

import { useNoticeStore } from "src/store/notice/notice.store";
import type { NoticeItem, NoticeTarget, NoticeUpsertPayload } from "src/types/notice/noticeTypes";

import {
  getNoticeListApi,
  createNoticeApi,
  updateNoticeApi,
  deleteNoticeApi,
  setNoticePinApi,
  setNoticeActiveApi,
} from "src/api/notice/noticeApi";

import { snackbarStore } from "src/store/snackbarStore";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function NoticeManagePage() {
  /** ✅ store에서는 query만 가져온다 (fetchList 같은 액션을 페이지에서 아예 안 씀) */
  const noticeTarget = useNoticeStore((s) => (s.query.noticeTarget as NoticeTarget) || "");
  const activeYn = useNoticeStore((s) => s.query.activeYn);
  const size = useNoticeStore((s) => s.query.size);
  const order = useNoticeStore(
    (s) => s.query.order || "pinnedYn,desc:noticeOrder,asc:createdAt,desc"
  );
  const page = useNoticeStore((s) => s.query.page);

  const setQuery = useNoticeStore((s) => s.setQuery);
  const resetQuery = useNoticeStore((s) => s.resetQuery);

  /** ✅ 입력은 로컬로 들고 있다가 검색 눌렀을 때만 query 반영 */
  const storeNoticeTtl = useNoticeStore((s) => s.query.noticeTtl || "");
  const [inputTtl, setInputTtl] = useState(storeNoticeTtl);

  useEffect(() => {
    setInputTtl((prev) => (prev === storeNoticeTtl ? prev : storeNoticeTtl));
  }, [storeNoticeTtl]);

  const activeYnUi = useMemo(() => {
    if (typeof activeYn !== "boolean") return "";
    return activeYn ? "true" : "false";
  }, [activeYn]);

  /** ✅ SWR key: query 파라미터가 바뀔 때만 자동 재조회 */
  const swrKey = useMemo(
    () => ["notice-list", storeNoticeTtl, noticeTarget, activeYn, page, size, order] as const,
    [storeNoticeTtl, noticeTarget, activeYn, page, size, order]
  );

  const { data, error, isValidating, mutate } = useSWR(
    swrKey,
    async () => {
      return await getNoticeListApi({
        noticeTarget: noticeTarget || undefined,
        noticeTtl: storeNoticeTtl?.trim() || undefined,
        activeYn: typeof activeYn === "boolean" ? activeYn : undefined,
        page,
        size,
        order,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const statusCode = (data as any)?.statusCode ?? (data as any)?.status_code;
  const ok = statusCode === undefined || statusCode === 0 || statusCode === 1;
  const apiErrorMsg = !ok ? (data as any)?.message || "공지사항 목록 로드 실패" : null;

  const list: NoticeItem[] = (data as any)?.data?.content ?? [];
  const totalPages = Math.max(1, (data as any)?.data?.totalPages ?? 1);
  const totalElements = (data as any)?.data?.totalElements ?? 0;

  /** ===== 모달 ===== */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<NoticeItem | null>(null);

  const openCreate = () => {
    setModalMode("create");
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (item: NoticeItem) => {
    setModalMode("edit");
    setEditing(item);
    setModalOpen(true);
  };

  const executeSearch = () => {
    // ✅ 여기서만 store query에 반영 (입력 타이핑 중엔 store 변경 없음)
    setQuery({ noticeTtl: inputTtl.trim(), page: 0 });
  };

  const onReset = () => {
    resetQuery();
    // resetQuery가 storeNoticeTtl을 바꾸면 위 useEffect가 input도 맞춰줌
  };

  /** ===== CRUD ===== */
  const onSubmit = async (payload: NoticeUpsertPayload, file?: File | null) => {
    try {
      if (modalMode === "create") {
        const res = await createNoticeApi(payload, file);
        const code = (res as any)?.statusCode ?? (res as any)?.status_code;
        if (!(code === 0 || code === 1)) {
          toast((res as any)?.message || "공지 등록 실패", "error");
          return false;
        }
        toast("공지사항이 등록되었습니다.", "success");
        await mutate();
        return true;
      }

      if (!editing) return false;

      const res = await updateNoticeApi(editing.noticeId, payload, file);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (!(code === 0 || code === 1)) {
        toast((res as any)?.message || "공지 수정 실패", "error");
        return false;
      }
      toast("공지사항이 수정되었습니다.", "success");
      await mutate();
      return true;
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
      return false;
    }
  };

  const onDelete = async (item: NoticeItem) => {
    if (!window.confirm("삭제할까요?")) return;
    try {
      const res = await deleteNoticeApi(item.noticeId);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (!(code === 0 || code === 1)) {
        toast((res as any)?.message || "공지 삭제 실패", "error");
        return;
      }
      toast("삭제되었습니다.", "success");
      await mutate();
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  const onTogglePinned = async (item: NoticeItem, pinnedYn: boolean) => {
    try {
      const res = await setNoticePinApi(item.noticeId, pinnedYn);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (!(code === 0 || code === 1)) {
        toast((res as any)?.message || "PIN 설정 실패", "error");
        return;
      }
      await mutate();
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  const onToggleActive = async (item: NoticeItem, activeYnNext: boolean) => {
    try {
      const res = await setNoticeActiveApi(item.noticeId, activeYnNext);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (!(code === 0 || code === 1)) {
        toast((res as any)?.message || "ACTIVE 설정 실패", "error");
        return;
      }
      await mutate();
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Box sx={{ overflowX: "auto" }}>
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            minWidth: 980,
            width: "max-content",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            공지사항 관리
          </Typography>

          <Divider sx={{ my: 2 }} />

          <NoticeFilterBar
            noticeTtl={inputTtl}
            onChangeNoticeTtl={setInputTtl}
            noticeTarget={noticeTarget}
            activeYn={activeYnUi as any}
            size={size}
            onChangeImmediate={(patch) => setQuery({ ...patch, page: 0 })}
            onSearch={executeSearch}
            onReset={onReset}
            onCreate={openCreate}
          />

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary" }}>
              총 {totalElements}건
            </Typography>

            {isValidating ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  불러오는 중...
                </Typography>
              </Stack>
            ) : null}
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {(error as any)?.message ?? "공지사항 목록 로드 실패"}
            </Alert>
          ) : null}

          {apiErrorMsg ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {apiErrorMsg}
            </Alert>
          ) : null}

          <Box sx={{ minWidth: 980 }}>
            <NoticeListTable
              list={list}
              onEdit={openEdit}
              onDelete={onDelete}
              onTogglePinned={onTogglePinned}
              onToggleActive={onToggleActive}
            />
          </Box>

          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={Math.min(page + 1, totalPages)}
              onChange={(_, v) => setQuery({ page: v - 1 })}
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Paper>
      </Box>

      <NoticeUpsertModal
        open={modalOpen}
        mode={modalMode}
        item={editing}
        loading={isValidating}
        onClose={() => setModalOpen(false)}
        onSubmit={onSubmit}
      />
    </Box>
  );
}
